import { create } from 'zustand'
import type { PersistStorage, StorageValue } from 'zustand/middleware'
import { persist } from 'zustand/middleware'

import type { AppMode } from '@/utils/appMode'
import {
  MANAGE_MODE_DEFAULT_PATH,
  WORK_MODE_DEFAULT_PATH,
  stripAppModeFromPath,
} from '@/utils/appMode'
import { createUserStorage } from './userStorage'

export type { AppMode } from '@/utils/appMode'
export {
  MANAGE_MODE_DEFAULT_PATH,
  WORK_MODE_DEFAULT_PATH,
} from '@/utils/appMode'

interface UserModeStoreState {
  lastActiveMode: AppMode
  lastManagePath: string
  lastWorkPath: string
}

const USER_MODE_STORE_KEY = 'user-mode-store'

const INITIAL_USER_MODE_STORE: UserModeStoreState = {
  lastActiveMode: 'manage',
  lastManagePath: MANAGE_MODE_DEFAULT_PATH,
  lastWorkPath: WORK_MODE_DEFAULT_PATH,
}

type LegacyUserModeStoreState = {
  lastActiveMode?: AppMode
  lastManagePath?: string
  lastWorkPath?: string
  activeMode?: AppMode
}

// `lastActiveMode` is per-tab (sessionStorage), so two tabs can be in
// different modes. `lastManagePath`/`lastWorkPath` are shared across tabs
// (localStorage) and also act as the default mode for tabs with no session
// value yet (new tab, tab restored after browser restart).
const localUserStorage = createUserStorage<UserModeStoreState>()
const sessionUserStorage = createUserStorage<UserModeStoreState>(sessionStorage)

const userModeStorage: PersistStorage<UserModeStoreState> = {
  getItem: (name) => {
    const localValue = localUserStorage.getItem(
      name,
    ) as StorageValue<UserModeStoreState> | null
    const sessionValue = sessionUserStorage.getItem(
      name,
    ) as StorageValue<UserModeStoreState> | null

    if (!localValue) {
      return null
    }

    return {
      ...localValue,
      state: {
        ...localValue.state,
        lastActiveMode:
          sessionValue?.state.lastActiveMode ?? localValue.state.lastActiveMode,
      },
    }
  },
  setItem: (name, value) => {
    localUserStorage.setItem(name, value)
    sessionUserStorage.setItem(name, value)
  },
  removeItem: (name) => {
    localUserStorage.removeItem(name)
    sessionUserStorage.removeItem(name)
  },
}

const useUserModeStore = create<UserModeStoreState>()(
  persist(() => INITIAL_USER_MODE_STORE, {
    name: USER_MODE_STORE_KEY,
    storage: userModeStorage,
    version: 1,
    migrate: (persistedState) => {
      const state = persistedState as LegacyUserModeStoreState

      return {
        lastActiveMode: state.lastActiveMode ?? state.activeMode ?? 'manage',
        lastManagePath: state.lastManagePath ?? MANAGE_MODE_DEFAULT_PATH,
        lastWorkPath: state.lastWorkPath ?? WORK_MODE_DEFAULT_PATH,
      }
    },
  }),
)

export const getLastActiveMode = (): AppMode =>
  useUserModeStore.getState().lastActiveMode

export const useLastActiveMode = (): AppMode =>
  useUserModeStore((state) => state.lastActiveMode)

export const setLastActiveMode = (mode: AppMode) =>
  useUserModeStore.setState({ lastActiveMode: mode })

export const getLastPathForMode = (mode: AppMode): string => {
  const state = useUserModeStore.getState()

  return mode === 'work' ? state.lastWorkPath : state.lastManagePath
}

export const updateLastPath = (mode: AppMode, path: string) => {
  // Temporary: strip legacy `?mode=` from URLs so it never gets stored.
  const cleanPath = stripAppModeFromPath(path)

  if (mode === 'manage') {
    useUserModeStore.setState({ lastManagePath: cleanPath })
  } else {
    useUserModeStore.setState({ lastWorkPath: cleanPath })
  }
}

export const rehydrateUserModeStore = () => useUserModeStore.persist.rehydrate()

export const resetUserModeStore = () =>
  useUserModeStore.setState(INITIAL_USER_MODE_STORE, true)
